import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MdOutlineCardGiftcard } from "react-icons/md";
import { motion } from "framer-motion";
import { IoFilter } from "react-icons/io5";
import { FaSearch, FaSort } from "react-icons/fa";
import { toast } from "react-toastify";
import {
  getProducts,
} from "../../services/ClientService";
import ChatbotWidget from "../ChatBot";

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(""); // state for search term
  const [filters, setFilters] = useState({}); // holds both filter status and sort key
  const [showFilterPopup, setShowFilterPopup] = useState(false); // toggle for filter popup
  const [showSortPopup, setShowSortPopup] = useState(false); // toggle for sort popup
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const PAGE_SIZE = 9;
  const navigate = useNavigate();

  // Helper function to get product image with fallback to option-specific images
  const getProductImage = (product) => {
    // Check main images first
    if (product.images && product.images.length > 0 && product.images[0]?.preview) {
      return product.images[0].preview;
    }
    // Fallback to first option's images
    if (product.productOptions && product.productOptions.length > 0) {
      const firstOption = product.productOptions[0];
      if (firstOption.images && firstOption.images.length > 0 && firstOption.images[0]?.preview) {
        return firstOption.images[0].preview;
      }
    }
    // Final fallback to placeholder
    return require("../../../src/no-image-icon-23494.png");
  };

  const fetchProducts = useCallback(async (params, page = 0, append = false) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setLoading(true);
      setCurrentPage(0);
      setHasMoreProducts(true);
    }
    try {
      const data = await getProducts({ ...params, skip: page * PAGE_SIZE, limit: PAGE_SIZE });
      if (data.success) {
        if (append) {
          setProducts(prev => [...prev, ...data.data]);
        } else {
          setProducts(data.data);
        }
        setHasMoreProducts(data.data.length === PAGE_SIZE);
        if (!append) setCurrentPage(0);
      } else {
        if (!append) setProducts([]);
        setHasMoreProducts(false);
      }
    } catch (error) {
      if (!append) setProducts([]);
      setHasMoreProducts(false);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchProducts({ searchTerm, ...filters }, 0, false);
    }, 800);
    return () => clearTimeout(debounceTimer);
  }, [fetchProducts, searchTerm, filters]);

  const handleCreateProduct = () => {
    navigate("/createProduct");
  };

  const handleNavigateToProductOverview = (productId) => {
    navigate(`/productOverview/${productId}`);
  };

  const loadMoreProducts = () => {
    if (isLoadingMore || !hasMoreProducts || loading) return;
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchProducts({ searchTerm, ...filters }, nextPage, true);
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 100 && !isLoadingMore && hasMoreProducts) {
      loadMoreProducts();
    }
  };

  // Clear only the filter (status) selection
  const handleClearFilterSelection = () => {
    const newFilters = { ...filters, isActive: [true, false] };
    delete newFilters.status;
    setFilters(newFilters);
    setShowFilterPopup(false);
  };

  // Clear only the sort selection
  const handleClearSortSelection = () => {
    const newFilters = { ...filters };
    delete newFilters.sort;
    delete newFilters.sortValue;
    setFilters(newFilters);
    setShowSortPopup(false);
  };

  // Handle selection of a filter value (Active/Inactive/Pending)
  const handleFilterChange = (status) => {
    const newFilters = { ...filters, status, isActive: [status === 'Inactive' ? false : true] };
    setFilters(newFilters);
    setShowFilterPopup(false);
  };

  // Update search term (debounce logic in useEffect)
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  return (
    // Added a container wrapper for proper centering on larger screens
    <div className="h-screen overflow-hidden max-h-fit bg-white relative">
      {/* Sticky Header */}
      {/* <NavigateBack target="/home" label="Return Products" className="pl-3" /> */}
      <div className="bg-white p-6 sticky top-0 z-10 space-y-4">
        <div className="flex justify-between items-center gap-4 flex-wrap">
          <h1 className="text-3xl font-bold">Products</h1>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={handleCreateProduct}
              className="relative text-white font-bold bg-gradient-to-r from-blue-500 to-purple-500 border-2 border-transparent rounded-full px-5 py-2.5 transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-xl group"
            >
              <span className="absolute inset-0 rounded-full border-2 border-blue-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out"></span>
              <span className="relative z-10 flex items-center">
                <MdOutlineCardGiftcard className="mr-2 text-2xl" />
                Create Product
              </span>
            </button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:space-x-4 relative">
          {/* Search Bar */}
          <div className="w-full relative">
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search Products"
              className="border border-gray-300 rounded-full py-2 pl-11 pr-4 w-full text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm hover:shadow-md transition-shadow duration-300"
            />
            <div className="absolute top-1/2 left-4 transform -translate-y-1/2 text-gray-400">
              <FaSearch className="text-lg" />
            </div>
          </div>
          {/* Buttons Container */}
          <div className="flex flex-row w-full md:w-auto max-md:pt-4">
            {/* Filters Button */}
            <motion.div
              key="filters"
              onClick={() => {
                setShowFilterPopup(!showFilterPopup);
                if (showSortPopup) setShowSortPopup(false); // close sort popup if open
              }}
              className="flex items-center px-4 py-2 rounded-full cursor-pointer hover:shadow-md text-sm font-semibold transition bg-white text-gray-700 border border-gray-300 shadow hover:bg-gray-100 hover:text-gray-900 focus:ring-2 focus:ring-blue-500 relative"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <IoFilter className="mr-2 text-lg" />
              Filters
              {/* Filters Popup */}
              {showFilterPopup && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full left-0 mt-2 w-40 bg-white border border-gray-200 rounded-md shadow-lg"
                >
                  {filters.status && (
                    <button
                      onClick={handleClearFilterSelection}
                      className="w-full text-left px-4 py-2 bg-red-400 text-white transition-colors duration-300 hover:bg-red-600"
                    >
                      Clear Filters
                    </button>
                  )}
                  <button
                    onClick={() => handleFilterChange("Active")}
                    className={`w-full text-left px-4 py-2 transition-colors duration-300 ${filters.status === "Active"
                      ? "bg-blue-500 text-white"
                      : "hover:bg-gray-100 text-gray-700"
                      }`}
                  >
                    Active
                  </button>
                  <button
                    onClick={() => handleFilterChange("Inactive")}
                    className={`w-full text-left px-4 py-2 transition-colors duration-300 ${filters.status === "Inactive"
                      ? "bg-blue-500 text-white"
                      : "hover:bg-gray-100 text-gray-700"
                      }`}
                  >
                    Inactive
                  </button>
                </motion.div>
              )}
            </motion.div>

            {/* Sort Button */}
            <motion.div
              key="sort"
              onClick={() => {
                setShowSortPopup(!showSortPopup);
                if (showFilterPopup) setShowFilterPopup(false); // close filter popup if open
              }}
              className="flex items-center px-4 py-2 ml-4 rounded-full cursor-pointer hover:shadow-md text-sm font-semibold transition bg-white text-gray-700 border border-gray-300 shadow hover:bg-gray-100 hover:text-gray-900 focus:ring-2 focus:ring-blue-500 relative"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaSort className="mr-2 text-lg" />
              Sort
              {/* Sort Popup */}
              {showSortPopup && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full right-0 mt-2 w-52 bg-white border border-gray-200 rounded-md shadow-lg"
                >
                  {filters.sort && (
                    <button
                      onClick={handleClearSortSelection}
                      className="w-full text-left px-4 py-2 bg-red-400 text-white transition-colors duration-300 hover:bg-red-600"
                    >
                      Clear Sort
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFilters((prev) => ({ ...prev, sort: "id", sortValue: 1 }));
                      setShowSortPopup(false);
                    }}
                    className={`w-full flex justify-between text-left px-4 py-2 transition-colors duration-300 ${filters.sort === "id" && filters.sortValue === 1
                      ? "bg-blue-500 text-white"
                      : "hover:bg-gray-100 text-gray-700"
                      }`}
                  >
                    <span>Sort by ID</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs opacity-80">(A to Z)</span>
                      <span className={`${filters.sort === "id" && filters.sortValue === 1 ? 'text-white' : 'text-gray-600'}`}>
                        ↓
                      </span>
                    </div>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFilters((prev) => ({ ...prev, sort: "id", sortValue: -1 }));
                      setShowSortPopup(false);
                    }}
                    className={`w-full flex justify-between text-left px-4 py-2 transition-colors duration-300 ${filters.sort === "id" && filters.sortValue === -1
                      ? "bg-blue-500 text-white"
                      : "hover:bg-gray-100 text-gray-700"
                      }`}
                  >
                    <span>Sort by ID</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs opacity-80">(Z to A)</span>
                      <span className={`${filters.sort === "id" && filters.sortValue === -1 ? 'text-white' : 'text-gray-600'}`}>
                        ↑
                      </span>
                    </div>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFilters((prev) => ({ ...prev, sort: "name", sortValue: 1 }));
                      setShowSortPopup(false);
                    }}
                    className={`w-full flex justify-between text-left px-4 py-2 transition-colors duration-300 ${filters.sort === "name" && filters.sortValue === 1
                      ? "bg-blue-500 text-white"
                      : "hover:bg-gray-100 text-gray-700"
                      }`}
                  >
                    <span>Sort by Name</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs opacity-80">(A to Z)</span>
                      <span className={`${filters.sort === "name" && filters.sortValue === 1 ? 'text-white' : 'text-gray-600'}`}>
                        ↓
                      </span>
                    </div>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFilters((prev) => ({ ...prev, sort: "name", sortValue: -1 }));
                      setShowSortPopup(false);
                    }}
                    className={`w-full flex justify-between text-left px-4 py-2 transition-colors duration-300 ${filters.sort === "name" && filters.sortValue === -1
                      ? "bg-blue-500 text-white"
                      : "hover:bg-gray-100 text-gray-700"
                      }`}
                  >
                    <span>Sort by Name</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs opacity-80">(Z to A)</span>
                      <span className={`${filters.sort === "name" && filters.sortValue === -1 ? 'text-white' : 'text-gray-600'}`}>
                        ↑
                      </span>
                    </div>
                  </button>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Products Section */}
      {loading ? (
        <div className="fixed inset-0 flex items-center justify-center bg-white/80 z-50">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="overflow-y-auto h-full max-h-[calc(100vh-270px)] md:max-h-[calc(100vh-170px)] px-6 bg-white scrollbar-thin" onScroll={handleScroll}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 my-3 mb-4">
            {products.length > 0 ? (
              products.map((product) => (
                <div
                  key={product._id}
                  onClick={() => handleNavigateToProductOverview(product._id)}
                  className={`group bg-white border cursor-pointer border-gray-200 shadow-lg hover:shadow-2xl rounded-2xl flex flex-col md:flex-row relative transition-all duration-300 overflow-hidden ${!product.isActive ? "bg-gray-100 border-gray-300 opacity-60" : ""}`}
                >
                  {/* Gray Overlay for Inactive Products */}
                  {!product.isActive && (
                    <div className="absolute inset-0 bg-gray-200 bg-opacity-50 rounded-2xl z-10"></div>
                  )}

                  {/* Product Image */}
                  <div className="w-full md:w-2/5 flex-shrink-0 flex items-center justify-center bg-gradient-to-tr from-gray-50 via-white to-blue-50 md:rounded-l-2xl rounded-t-2xl md:rounded-tr-none h-48 md:h-auto relative max-h-[226px]">
                    {/* Status Badge */}
                    <img
                      src={getProductImage(product)}
                      alt={`Product ${product.productName}`}
                      className="w-4/5 h-4/5 object-contain scale-95 transition-transform duration-300 drop-shadow-lg"
                    />
                  </div>

                  {/* Product Details */}
                  <div className="p-5 flex flex-col justify-between flex-grow w-full md:w-3/5 space-y-3">
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <h2 className={`text-lg font-bold ${product.isActive ? "text-gray-900" : "text-gray-500"} line-clamp-2 flex-1`}>
                          {product.productName}
                        </h2>
                        <span className={`text-xs font-semibold ${product.isActive ? "text-blue-700 bg-blue-100" : "text-gray-500 bg-gray-200"} px-2 py-1 rounded-full shrink-0`}>
                          {product.productId}
                        </span>
                      </div>
                      <p className={`text-base line-clamp-2 ${product.isActive ? "text-gray-700" : "text-gray-400"}`}>
                        {product.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {(product.productOptions || []).map((option, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-full shadow-sm"
                          >
                            {typeof option.weight === 'object' && option.weight !== null 
                              ? option.weight.weightLabel || `${option.weight.weightValue} ${option.weight.metricLabel}`
                              : option.weight}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                      <span className={`flex items-center gap-2 ${product.isOutOfStock ? "text-red-600" : "text-green-600"} text-sm font-medium`}>
                        <span
                          className={`relative flex h-2 w-2 items-center justify-center ${product.isOutOfStock ? "bg-red-600" : "bg-green-600"} rounded-full`}
                        >
                          <span
                            className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${product.isOutOfStock ? "bg-red-400" : "bg-green-400"} animate-ping`}
                          ></span>
                        </span>
                        {product.isOutOfStock ? "Out of stock" : "In stock"}
                      </span>
                      <span
                        className={`px-3 py-1 text-xs font-semibold rounded-full shadow ${product.isActive ? "bg-green-100 text-green-600" : "bg-gray-300 text-gray-700"}`}
                      >
                        {product.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p>No Products found</p>
            )}
          </div>
          {isLoadingMore && (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          )}
        </div>
      )}


      {/* <ChatbotWidget /> */}
    </div>
  );
};

export default Products;
